import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DocumentSubType,
  ExtraUser,
  Objection,
  ObjectionVariant,
} from "@/briefpoint-client";
import type { DocumentDetailClient } from "@/lib/types";
import { useObjectionApi } from "./useApi";

export type UpdateVariantFunctionType = (
  objectionId: number,
  variantId: number,
  name: string,
  newContent: string,
  isDefault: boolean,
  additionalContent?: string,
  sharedWithFirmId?: string,
  refreshValues?: boolean,
  caseTypeId?: string | null,
  tagIds?: Array<number> | null,
  documentSubType?: DocumentSubType,
) => Promise<void>;

export type CreateVariantFunctionType = (
  objectionId: number,
  name: string,
  newContent: string,
  isDefault: boolean,
  additionalContent?: string,
  sharedWithFirmId?: string,
  refreshValues?: boolean,
  caseTypeId?: string | null,
  tagIds?: Array<number> | null,
  documentSubType?: DocumentSubType,
) => Promise<ObjectionVariant>;

export type DeleteVariantFunctionType = (
  objectionId: number,
  variantId: number,
) => Promise<void>;

export type getDefaultObjectionType = (
  objectionId: number,
) => Promise<ObjectionVariant>;

type UseObjectionsType = [
  Objection[] | undefined,
  UpdateVariantFunctionType,
  CreateVariantFunctionType,
  getDefaultObjectionType,
  DeleteVariantFunctionType,
  ExtraUser[] | undefined,
];

export default function useObjections(
  documentType: number | undefined = undefined,
  document?: DocumentDetailClient,
): UseObjectionsType {
  const objectionApi = useObjectionApi();
  const allObjections = useRef<Objection[] | undefined>(undefined);
  const currentDocId = useRef<string | undefined>(undefined);
  const [objections, setObjections] = useState<Objection[] | undefined>();
  const [extraUsers, setExtraUsers] = useState<ExtraUser[] | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const loadObjections = useCallback(
    async (clearCached = false) => {
      if (loading) {
        return;
      }
      // Clear cache when document changes or when explicitly requested
      if (
        clearCached ||
        (document?.id && currentDocId.current !== document.id)
      ) {
        allObjections.current = undefined;
        currentDocId.current = document?.id;
      }

      if (!loading && !allObjections.current?.length) {
        setLoading(true);
        const objections = await objectionApi.objectionGetList({
          documentType: documentType,
        });
        allObjections.current = objections;
        setLoading(false);
      }

      if (document?.id) {
        const currentObjections = allObjections.current;

        if (currentDocId.current === document?.id) {
          const documentSubType = document?.info?.documentSubType;
          setObjections(
            currentObjections?.filter(
              (objection) =>
                objection.documentSubType === undefined ||
                !documentSubType ||
                objection.documentSubType === documentSubType,
            ),
          );

          const missingVariants = [] as string[];
          for (let o = 0; o < (allObjections.current?.length ?? 0); o++) {
            const objection = allObjections.current?.[o];

            if (!objection) {
              continue;
            }

            for (let i = 0; i < (document?.interrogatories.length ?? 0); i++) {
              const interrogatory = document.interrogatories[i];

              const selected = interrogatory.response[objection.id];
              const selectedId = selected?.objectionVariantId.toString();
              if (
                selectedId &&
                !missingVariants.find((x) => x === selectedId) &&
                !objection.variants.find(
                  (x) => x.id === selected.objectionVariantId,
                )
              ) {
                missingVariants.push(selectedId);
              }
            }
          }

          if (missingVariants.length || document.caseId) {
            const supplemental = await objectionApi.objectionGetMissing({
              documentType,
              caseId: document.caseId ?? undefined,
              missingVariants,
            });
            setObjections((x) =>
              x?.map((o) => {
                o.variants = [
                  ...o.variants,
                  ...(supplemental.variants?.filter(
                    (v) => v.objectionId === o.id,
                  ) ?? []),
                ];
                return o;
              }),
            );
            setExtraUsers(supplemental.users ?? []);
          }
        }
      } else {
        setObjections(allObjections.current);
      }
    },
    [loading, document, objectionApi, documentType],
  );

  async function updateVariant(
    objectionId: number,
    variantId: number,
    name: string,
    newContent: string,
    isDefault: boolean,
    additionalContent?: string,
    sharedWithFirmId?: string,
    refreshValues = true,
    caseTypeId?: string | null,
    tagIds?: Array<number> | null,
    documentSubType?: DocumentSubType,
  ) {
    // set objections first to update interface more quickly.
    // in case of error we probably want to revert.
    setObjections(
      objections?.map((o) =>
        o.id === objectionId
          ? {
              ...o,
              variants: o.variants.map((v) =>
                v.id === variantId
                  ? {
                      ...v,
                      name,
                      content: newContent,
                      isDefault,
                      additionalContent,
                    }
                  : { ...v, isDefault: isDefault ? false : v.isDefault },
              ),
            }
          : o,
      ),
    );
    await objectionApi.objectionUpdateVariant({
      objectionId,
      variantId,
      updateObjectionVariant: {
        name,
        content: newContent,
        isDefault,
        additionalContent,
        sharedWithFirmId,
        caseTypeId,
        tagIds,
        documentSubType,
      },
    });
    if (refreshValues) {
      loadObjections(true);
    }
  }

  async function createVariant(
    objectionId: number,
    name: string,
    content: string,
    isDefault: boolean,
    additionalContent?: string,
    sharedWithFirmId?: string,
    refreshValues = true,
    caseTypeId?: string | null,
    tagIds?: Array<number> | null,
    documentSubType?: DocumentSubType,
  ) {
    const variant = await objectionApi.objectionCreateVariant({
      objectionId,
      createObjectionVariant: {
        name,
        content,
        isDefault,
        objectionId,
        additionalContent,
        sharedWithFirmId,
        caseTypeId,
        tagIds,
        documentSubType,
      },
    });
    setObjections(
      objections?.map((o) =>
        o.id === objectionId
          ? {
              ...o,
              variants: [
                ...o.variants.map((v) => {
                  return { ...v, isDefault: isDefault ? false : v.isDefault };
                }),
                variant,
              ],
            }
          : o,
      ),
    );
    if (refreshValues) {
      loadObjections(true);
    }
    return variant;
  }

  async function getDefaultObjection(objectionId: number) {
    const defaultObj = await objectionApi.objectionGetDefault({
      objectionId,
    });
    return defaultObj;
  }

  async function deleteVariant(objectionId: number, variantId: number) {
    await objectionApi.objectionDeleteVariant({
      objectionId,
      variantId,
    });
    loadObjections(true);
  }

  useEffect(() => {
    loadObjections();
    // loadTags();
  }, [loadObjections]);

  return [
    objections,
    updateVariant,
    createVariant,
    getDefaultObjection,
    deleteVariant,
    extraUsers,
  ];
}
